import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2'; // Import SweetAlert2
import axios from 'axios'; // Import axios

const Blood = () => {
  document.title = 'Blood Type | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
  }); // Updated form data state
  const [editMode, setEditMode] = useState(false); // State for edit mode
  const [editId, setEditId] = useState<number | null>(null); // State for edit ID
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [tableData, setTableData] = useState<{ id: number; nama: string; created_at: string; updated_at: string }[]>([]); // Ensure default value is an empty array



  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
    setFormData({ nama: '' });
  };

  const handleEditModal = (blood: { id: number; nama: string }) => {
    setEditMode(true);
    setEditId(blood.id);
    setFormData({ nama: blood.nama });
    setShowModal(true);
  };



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value.toUpperCase() });
  };



  const handleFormSubmit = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      let response;
      if (editMode && editId) {
        // Update existing blood type
        response = await axios.patch(
          `/master-blood/${editId}`,
          {
            nama: formData.nama,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Blood Type "${response.data.nama}" updated successfully!`, 'success');
      } else {
        // Create new blood type
        response = await axios.post(
          '/master-blood',
          {
            nama: formData.nama,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Blood Type "${response.data.nama}" added successfully!`, 'success');
      }

      setShowModal(false);
      setEditMode(false);
      setEditId(null);
      setFormData({ nama: '' }); // Reset form
      fetchBlood(); // Refetch blood types to update the table
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };



  const fetchBlood = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-blood', {
        headers: {
          Authorization: `Bearer ${token}`, // Add token to Authorization header
        },
      });

      setTableData(response.data.data || []); // Update table data with API response
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000, // Smooth transition after loading
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch blood types',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true); // Start loading
        try {
          const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.delete(`/master-blood/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          });

          Swal.fire('Deleted!', 'The blood type has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id)); // Remove deleted item from table
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to delete blood type',
            'error'
          );
        } finally {
          setIsLoading(false); // End loading
        }
      }
    });
  };



  useEffect(() => {
    fetchBlood(); // Fetch blood types on component mount
  }, []); // Fetch blood types on component mount

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Blood Type" title="Blood Type" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0"></h5>
                    <div className="ms-auto"> {/* Add ms-auto to push the button to the right */}
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Golongan Darah</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? ( // Ensure tableData is an array and not empty
                        tableData.map((blood, index) => (
                          <tr key={blood.id}>
                            <td>{index + 1}</td>
                            <td>{blood.nama}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="dark" id={`action-dropdown-${blood.id}`} className="btn-sm">
                                  <i className="ri-more-2-fill"></i> {/* Add an icon for the dropdown toggle */}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleEditModal(blood)}>
                                    <i className="ri-edit-line me-2"></i> Edit {/* Add an edit icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDelete(blood.id)}>
                                    <i className="ri-delete-bin-line me-2"></i> Delete {/* Add a delete icon */}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center">
                            No blood types available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding Blood Type */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Blood Type' : 'Add Blood Type'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="nama">Golongan Darah</Form.Label>
                <Form.Control
                  type="text"
                  id="nama"
                  placeholder="Enter the blood type (e.g., A, B, AB, O)"
                  value={formData.nama}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.nama.trim()} // Validation feedback
                  style={{ textTransform: 'uppercase' }}
                />
                <Form.Control.Feedback type="invalid">
                  Golongan Darah is required.
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={handleCloseModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>


      </div>
    </React.Fragment>
  );
};

export default Blood;
